import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { Snackbar } from "./snackbar";
import { Alert } from "./alert";

type Severity = "error" | "warning" | "info" | "success";

interface NotificationState {
  open: boolean;
  message: string | ReactNode;
  severity: Severity;
}

export interface NotificationArgs {
  msg: string | ReactNode;
  severity?: Severity;
}

type NotificationContextType = {
  notify: (args: NotificationArgs) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotify = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotify must be used within a NotificationProvider");
  }
  return context;
};

type NotificationProviderProps = {
  children: ReactNode;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    severity: "info",
  });

  const notify = ({ msg, severity = "info" }: NotificationArgs) => {
    setNotification({ open: true, message: msg, severity: severity });
  };

  const hideAlert = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={hideAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={hideAlert}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
